import { useState, useEffect, useCallback } from 'react'
import { useOrgParams } from '~/common/use-org-params'
import { supabaseGetter } from '~/api/supabase-getter'

export const useFarmChildren = ({ type = 'plot', farmId: farmIdProp } = {}) => {
  const { farmId: urlFarmId } = useOrgParams()
  const farmId = farmIdProp ?? urlFarmId
  const [childOrgs, setChildOrgs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    let isMounted = true

    async function fetchPoints() {
      if (!farmId) {
        if (isMounted) {
          setChildOrgs([])
          setLoading(false)
          setError(null)
        }
        return
      }

      try {
        if (isMounted) {
          setLoading(true)
          setError(null)
        }

        // ALWAYS fetch all plots when farmId exists
        const { data: childPlotOrgs, error: plotOrgsError } = await supabaseGetter.organizations.getPlots(farmId)
        if (!isMounted) return
        if (plotOrgsError) throw plotOrgsError
        if (!childPlotOrgs) throw new Error('Child plot organizations data empty')

        setChildOrgs(childPlotOrgs)

        if (type === 'patch') {
          const allPatchOrgs = await Promise.all(childPlotOrgs.map(async (plot) => {
            const { data: childPatchOrgs, error: patchOrgsError } = await supabaseGetter.organizations.getPatches(plot.id)
              if (patchOrgsError) throw patchOrgsError
              if (!childPatchOrgs) throw new Error('Child patch organizations data empty')

              return childPatchOrgs
          }))

          if (!isMounted) return

          setChildOrgs([...childPlotOrgs, ...allPatchOrgs.flat()])
        }
      } catch (err) {
        if (isMounted) {
          setChildOrgs([])
          setLoading(false)
          setError('An unexpected error occurred:', err)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchPoints()

    return () => {
      isMounted = false
    }
  }, [farmId, type, refreshKey])

  const invalidate = useCallback(() => {
    supabaseGetter.organizations.invalidate()
    setRefreshKey(k => k + 1)
  }, [])

  return {
    childOrgs,
    loading,
    error,
    invalidate,
  }
}
